import {
  Tenancy_Node_Type_Enum_Ps_Enum,
  Tenancy_Rich_Asset_Type_Enum_Ps_Enum,
} from '@pypestream/api-services';

import {
  HANDOFF_TARGET,
  RETURN_TARGET,
  type AgentSpec,
} from '../../utils/agent-spec';
import { defaultRowData, type TableNode } from '../table-store';

/**
 * Project Bobcat — an agent spec (a template or an AI draft) as table rows,
 * numbered from `numbers` in step order (see `specRowCount`), with every
 * `ref` resolved:
 *
 * - `return` points at `returnTo` (the Supervisor's first node), or nowhere
 *   when there isn't one: the step ends the agent.
 * - `handoff` points at one shared hand-off step, added at the end only when
 *   something uses it — a Decision node with `xfer_to_agent`.
 *
 * The caller allocates the numbers (free ones, next to where the agent sits);
 * the first is the agent's first step.
 */
export const HANDOFF_MESSAGE =
  'Let me connect you with someone on our team. I’ll pass along what you’ve told me, so you won’t have to repeat it.';

const usesHandoff = (spec: AgentSpec): boolean =>
  spec.steps
    .flatMap((step) =>
      step.type === 'A'
        ? step.outcomes.map((o) => o.next)
        : [step.next, ...(step.buttons ?? []).map((b) => b.next)]
    )
    .includes(HANDOFF_TARGET);

/** How many rows `specToRows` makes: the steps, plus the hand-off if used. */
export const specRowCount = (spec: AgentSpec): number =>
  spec.steps.length + (usesHandoff(spec) ? 1 : 0);

export const specToRows = (
  spec: AgentSpec,
  { numbers, returnTo }: { numbers: number[]; returnTo: number | null }
): TableNode[] => {
  if (numbers.length < specRowCount(spec)) {
    throw new Error('specToRows: not enough node numbers for the spec.');
  }
  const numberOf = new Map<string, number>();
  spec.steps.forEach((step, i) => numberOf.set(step.ref, numbers[i]));
  const handoffNumber = usesHandoff(spec) ? numbers[spec.steps.length] : null;

  /** A target as a node number; null for "nowhere". */
  const resolve = (target: string | undefined): number | null => {
    if (!target) return null;
    if (target === RETURN_TARGET) return returnTo;
    if (target === HANDOFF_TARGET) return handoffNumber;
    return numberOf.get(target) ?? handoffNumber;
  };

  const rows: TableNode[] = spec.steps.map((step, i) => {
    const nodeNumber = numbers[i];
    if (step.type === 'A') {
      const whatsNext = step.outcomes.flatMap((o) => {
        const n = resolve(o.next);
        return n === null ? [] : [`${o.value}~${n}`];
      });
      return {
        ...defaultRowData,
        nodeNumber: String(nodeNumber),
        nodeType: Tenancy_Node_Type_Enum_Ps_Enum.A,
        nodeName: step.name,
        command: step.command,
        parameterInput: JSON.stringify(step.parameterInput ?? {}, null, 2),
        decisionVariable: step.decisionVariable,
        // What the command returns: the compiler requires the decision
        // variable to be one of them.
        output: [step.decisionVariable],
        whatsNext,
        variables: step.variables?.length ? step.variables : [''],
      } as unknown as TableNode;
    }

    const next = resolve(step.next);
    const buttons = (step.buttons ?? []).flatMap((b) => {
      const n = resolve(b.next);
      return n === null ? [] : [`${b.label}~${n}`];
    });
    return {
      ...defaultRowData,
      nodeNumber: String(nodeNumber),
      nodeType: Tenancy_Node_Type_Enum_Ps_Enum.D,
      nodeName: step.name,
      message: step.message,
      answerRequired: !!step.answerRequired || buttons.length > 0,
      nluDisabled: !!step.nluDisabled,
      nextNodes: next === null ? [] : [next],
      variables: step.variables?.length ? step.variables : [''],
      ...(buttons.length
        ? {
            richAssetType: Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Button,
            richAssetContent: buttons.join('|'),
            behaviors: ['disable_input'],
          }
        : {}),
    } as TableNode;
  });

  if (handoffNumber !== null) {
    rows.push({
      ...defaultRowData,
      nodeNumber: String(handoffNumber),
      nodeType: Tenancy_Node_Type_Enum_Ps_Enum.D,
      nodeName: 'Hand off to a person',
      message: HANDOFF_MESSAGE,
      behaviors: ['xfer_to_agent'],
    } as TableNode);
  }

  return rows;
};
